import React, { memo, useCallback, useMemo } from "react";
import {
  Box,
  Button,
  Popover,
  PopoverArrow,
  PopoverContent,
  PopoverTrigger,
  SimpleGrid,
  useDisclosure,
} from "@chakra-ui/react";
import { IQuillFormats } from "modules/quill/use-quill";
import colors from "app/colors";
import { FontHighlightColorIcon } from "modules/icons";
import RemoveTextBackgroundIcon from "modules/icons/remove-text-background-icon";

const BACKGROUND_COLORS = [
  "#FFF200",
  "#88FF00",
  "#00FFFF",
  "#00BF33",
  "#FF0000",
  "#0066FF",
  "#A102D5",
  "#FFA500",
  "#000000",
];

const COLORS_WITH_BACKGROUND = ["#FFFFFF", "#FFF200"];

interface IProps {
  format: (name: string, value: any) => void;
  activeFormats: IQuillFormats;
}

const ComposeQuillTextBackgroundColorSelector = ({
  format,
  activeFormats,
}: IProps) => {
  const { isOpen, onOpen, onClose } = useDisclosure();

  const backgroundColor = Array.isArray(activeFormats.background)
    ? undefined
    : activeFormats.background;

  const isSelected = useMemo(
    () =>
      !!backgroundColor &&
      BACKGROUND_COLORS.includes(backgroundColor.toUpperCase()),
    [backgroundColor],
  );

  const color = useMemo(
    () =>
      !!backgroundColor &&
      BACKGROUND_COLORS.includes(backgroundColor.toUpperCase())
        ? backgroundColor
        : colors.gray40,
    [backgroundColor],
  );

  const bg = useMemo(() => {
    if (typeof color === "string") {
      if (isSelected && COLORS_WITH_BACKGROUND.includes(color.toUpperCase())) {
        return colors.gray30;
      }
    }
    return colors.transparent;
  }, [color, isSelected]);

  const onClick = useCallback(
    (color: string) => {
      format("background", color);
      onClose();
    },
    [format, onClose],
  );

  const onRemoveFormatClicked = useCallback(() => {
    format("background", null);
  }, [format]);

  return (
    <Popover
      placement="bottom-start"
      isOpen={isOpen}
      onOpen={onOpen}
      onClose={onClose}
    >
      <PopoverTrigger>
        <Button
          variant="hover"
          size="sm"
          aria-label="Format background text"
          tabIndex={-1}
          px={0}
        >
          <Box
            bg={bg}
            w={5}
            h={5}
            borderRadius={4}
            display="flex"
            justifyContent="center"
          >
            <FontHighlightColorIcon
              boxSize={4}
              fill={color}
              bg={bg}
              borderRadius={4}
            />
          </Box>
        </Button>
      </PopoverTrigger>
      <PopoverContent w="auto" p={2}>
        <PopoverArrow />
        <SimpleGrid columns={5} spacing={1}>
          <Box
            key={color}
            w={6}
            h={6}
            borderWidth={1}
            borderColor={colors.gray30}
            borderRadius="4px"
            cursor="pointer"
            display="flex"
            alignItems="center"
            justifyContent="center"
            _hover={{ transform: "scale(1.1)" }}
            onMouseDown={(e: React.MouseEvent) => {
              e.preventDefault();
              onRemoveFormatClicked();
            }}
          >
            <RemoveTextBackgroundIcon boxSize={5} fill={colors.gray30} />
          </Box>
          {BACKGROUND_COLORS.map((color) => (
            <Box
              key={color}
              w={6}
              h={6}
              bg={color}
              borderWidth={1}
              borderColor={colors.gray30}
              borderRadius="4px"
              cursor="pointer"
              _hover={{ transform: "scale(1.1)" }}
              onMouseDown={(e: React.MouseEvent) => {
                e.preventDefault();
                onClick(color);
              }}
            />
          ))}
        </SimpleGrid>
      </PopoverContent>
    </Popover>
  );
};

export default memo(ComposeQuillTextBackgroundColorSelector);
